import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, ArrowLeft, Upload, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { submitQuiz, getCloudinarySig } from "../lib/api";

const STEPS = [
    { id: "contact", label: "About you" },
    { id: "household", label: "Who you cook for" },
    { id: "diet", label: "Diet" },
    { id: "preferences", label: "Preferences" },
    { id: "style", label: "Style" },
    { id: "time", label: "Time" },
    { id: "ingredients", label: "Your kitchen" },
    { id: "challenge", label: "Biggest challenge" },
    { id: "personal", label: "Personalize", optional: true },
];

const HOUSEHOLD_OPTS = [
    "Just me",
    "Me + partner",
    "Family (with kids)",
    "Family (with elderly)",
    "Guests occasionally",
];

const DIET_OPTS = [
    "Jain (strict)",
    "Vegetarian (no eggs)",
    "Vegan",
    "No preference",
];

const PREFERENCE_OPTS = [
    "No onion / no garlic",
    "Sattvic (light, pure meals)",
    "Kid-friendly",
    "High-protein",
    "Comfort / homestyle food",
    "Traditional / festival meals",
    "None",
];

const STYLE_OPTS = [
    "Simple home-style Indian",
    "Traditional / regional dishes",
    "Light & sattvic meals",
    "Modern / quick fusion",
    "I like variety",
];

const TIME_OPTS = [
    "15 mins or less",
    "30 mins",
    "45 mins",
    "1 hour+",
];

const INGREDIENT_OPTS = [
    "Basic staples (atta, rice, dal, spices)",
    "Fresh vegetables",
    "Paneer / tofu",
    "Frozen foods",
    "Snacks / ready-to-eat",
];

const CHALLENGE_OPTS = [
    "I don't know what to cook",
    "It takes too much time",
    "I keep repeating the same meals",
    "My family/kids are picky",
    "I want healthier options",
    "Recipes feel too complicated",
];

export default function Quiz() {
    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Capture pricing-intent from CTA (?package=usd|inr) and stash for Summary to pre-highlight
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const pkg = params.get("package");
        if (pkg === "usd" || pkg === "inr") {
            try { sessionStorage.setItem("preferred_package", pkg); } catch (e) { /* ignore */ }
        }
    }, [location.search]);

    const [data, setData] = useState({
        full_name: "",
        email: "",
        phone: "",
        city: "",
        state: "",
        household: [],            // multi
        meal_preference: "",      // single (diet)
        preferences: [],          // multi
        cuisine_style: "",        // single (style)
        cooking_time: "",         // single
        ingredients: [],          // multi
        avoid_ingredients: "",    // optional text
        biggest_struggle: [],     // multi (challenges)
        assistant_name: "",
        photo_url: "",
    });

    useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

    const update = (k, v) => setData(d => ({ ...d, [k]: v }));
    const toggleInArray = (k, v) => setData(d => {
        const arr = d[k] || [];
        return { ...d, [k]: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v] };
    });

    const canNext = () => {
        switch (step) {
            case 0: return data.full_name.trim() && /^\S+@\S+\.\S+$/.test(data.email);
            case 1: return data.household.length > 0;
            case 2: return !!data.meal_preference;
            case 3: return data.preferences.length > 0;
            case 4: return !!data.cuisine_style;
            case 5: return !!data.cooking_time;
            case 6: return data.ingredients.length > 0;
            case 7: return data.biggest_struggle.length > 0;
            case 8: return true;
            default: return false;
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await submitQuiz(data);
            navigate(`/summary/${res.submission_id}`);
        } catch (e) {
            toast.error("Something went wrong. Please try again.");
            setSubmitting(false);
        }
    };

    const next = () => {
        if (!canNext()) { toast.error("Please complete this step."); return; }
        if (step === STEPS.length - 1) handleSubmit();
        else setStep(s => s + 1);
    };

    const back = () => setStep(s => Math.max(0, s - 1));

    const handlePhoto = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB please"); return; }
        setUploading(true);
        try {
            const sig = await getCloudinarySig("cooking-assistant/uploads");
            const form = new FormData();
            form.append("file", file);
            form.append("api_key", sig.api_key);
            form.append("timestamp", sig.timestamp);
            form.append("signature", sig.signature);
            form.append("folder", sig.folder);
            const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: form });
            const json = await res.json();
            if (!json.secure_url) throw new Error("Upload failed");
            update("photo_url", json.secure_url);
            toast.success("Photo uploaded ✨");
        } catch (e) {
            toast.error("Upload failed. Try a smaller image.");
        } finally {
            setUploading(false);
        }
    };

    const progress = ((step + 1) / STEPS.length) * 100;

    return (
        <div className="min-h-screen bg-bone" data-testid="quiz-page">
            <div className="bg-ink text-bone">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-saffron text-lg leading-none">✺</span>
                        <span className="font-heading text-lg font-semibold">My Cooking <em className="text-saffron not-italic">Assistant</em></span>
                    </div>
                    <button onClick={() => navigate("/")} className="text-bone/70 hover:text-saffron" data-testid="quiz-close" aria-label="close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-3">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-bone/70 mb-2">
                        <span data-testid="quiz-step-label">Step {step + 1} / {STEPS.length} · {STEPS[step].label}{STEPS[step].optional ? " (optional)" : ""}</span>
                        <span>~ {Math.max(10, 70 - step * 8)}s left</span>
                    </div>
                    <div className="w-full h-1 bg-bone/15 rounded-full overflow-hidden" data-testid="quiz-progress">
                        <div className="h-full bg-saffron transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-32">
                {step === 0 && (
                    <StepWrap title={<>Let's start with the <em className="text-saffron-dark font-medium">basics</em></>} sub="Just so we know who to send your assistant to.">
                        <div className="space-y-4">
                            <div>
                                <label className="label-strong">Your name</label>
                                <input className="input-field" data-testid="input-name" value={data.full_name} onChange={e => update("full_name", e.target.value)} placeholder="Priya Sharma" />
                            </div>
                            <div>
                                <label className="label-strong">Email</label>
                                <input className="input-field" type="email" data-testid="input-email" value={data.email} onChange={e => update("email", e.target.value)} placeholder="you@email.com" />
                            </div>
                            <div>
                                <label className="label-strong">WhatsApp / Phone <span className="text-muted2 normal-case font-normal tracking-normal">(optional)</span></label>
                                <input className="input-field" data-testid="input-phone" value={data.phone} onChange={e => update("phone", e.target.value)} placeholder="+91 98765 43210" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="label-strong">City <span className="text-muted2 normal-case font-normal tracking-normal">(optional)</span></label>
                                    <input className="input-field" data-testid="input-city" value={data.city} onChange={e => update("city", e.target.value)} placeholder="Mumbai" />
                                </div>
                                <div>
                                    <label className="label-strong">State <span className="text-muted2 normal-case font-normal tracking-normal">(optional)</span></label>
                                    <input className="input-field" data-testid="input-state" value={data.state} onChange={e => update("state", e.target.value)} placeholder="Maharashtra" />
                                </div>
                            </div>
                        </div>
                    </StepWrap>
                )}

                {step === 1 && (
                    <StepWrap title="Who are you usually cooking for?" sub="Tap all that apply.">
                        <MultiChoices testId="household" values={data.household} onToggle={v => toggleInArray("household", v)} options={HOUSEHOLD_OPTS} />
                    </StepWrap>
                )}

                {step === 2 && (
                    <StepWrap title="Which best describes your diet?" sub="Pick one.">
                        <Choices testId="diet" value={data.meal_preference} onChange={v => update("meal_preference", v)} options={DIET_OPTS} />
                    </StepWrap>
                )}

                {step === 3 && (
                    <StepWrap title="Any specific food preferences?" sub="Tap all that apply.">
                        <MultiChoices testId="prefs" values={data.preferences} onToggle={v => toggleInArray("preferences", v)} options={PREFERENCE_OPTS} />
                    </StepWrap>
                )}

                {step === 4 && (
                    <StepWrap title="What kind of meals do you enjoy most?" sub="Your everyday vibe.">
                        <Choices testId="style" value={data.cuisine_style} onChange={v => update("cuisine_style", v)} options={STYLE_OPTS} />
                    </StepWrap>
                )}

                {step === 5 && (
                    <StepWrap title="How much time do you usually have to cook?" sub="On a typical weekday.">
                        <Choices testId="time" value={data.cooking_time} onChange={v => update("cooking_time", v)} options={TIME_OPTS} />
                    </StepWrap>
                )}

                {step === 6 && (
                    <StepWrap title="What do you usually have at home?" sub="Tap all that apply.">
                        <MultiChoices testId="ingredients" values={data.ingredients} onToggle={v => toggleInArray("ingredients", v)} options={INGREDIENT_OPTS} />
                        <div className="mt-5">
                            <label className="label-strong">Add anything specific <span className="text-muted2 normal-case font-normal tracking-normal">(optional)</span></label>
                            <textarea
                                className="input-field min-h-[80px]"
                                data-testid="input-avoid"
                                value={data.avoid_ingredients}
                                onChange={e => update("avoid_ingredients", e.target.value)}
                                placeholder="e.g. always have curd, avoid mushrooms, kids love pasta…"
                            />
                        </div>
                    </StepWrap>
                )}

                {step === 7 && (
                    <StepWrap title="What's your biggest challenge when cooking?" sub="Tap all that apply. (Replies in English, ગુજરાતી, or हिन्दी — whatever's easiest at the moment.)">
                        <MultiChoices testId="challenge" values={data.biggest_struggle} onToggle={v => toggleInArray("biggest_struggle", v)} options={CHALLENGE_OPTS} />
                    </StepWrap>
                )}

                {step === 8 && (
                    <StepWrap title={<>Make it <em className="text-saffron-dark font-medium">yours</em></>} sub="Give your assistant a name and a face — totally optional.">
                        <div className="space-y-5">
                            <div>
                                <label className="label-strong">What should we call your assistant?</label>
                                <input className="input-field" data-testid="input-assistant-name" value={data.assistant_name} onChange={e => update("assistant_name", e.target.value)} placeholder="e.g. Mom's Kitchen, Sejal Aunty, My Cooking Guide…" />
                            </div>
                            <div>
                                <label className="label-strong">Upload a photo</label>
                                <p className="text-sm text-muted2 mb-3 normal-case tracking-normal">Adds a personal touch — totally optional 😊</p>
                                <label className="block">
                                    <input type="file" accept="image/*" className="hidden" data-testid="input-photo" onChange={e => handlePhoto(e.target.files?.[0])} />
                                    <div className="border-2 border-dashed border-ink rounded-2xl p-6 text-center bg-white cursor-pointer hover:bg-saffron/10 transition-colors">
                                        {uploading ? (
                                            <span className="flex items-center justify-center gap-2 text-muted2"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
                                        ) : data.photo_url ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <img src={data.photo_url} alt="upload" className="w-24 h-24 object-cover rounded-full border-2 border-ink" data-testid="photo-preview" />
                                                <span className="text-sm text-teal flex items-center gap-1 font-medium"><Check className="w-4 h-4" /> Uploaded — tap to change</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 text-muted2">
                                                <Upload className="w-6 h-6" />
                                                <span className="text-sm">Tap to upload a photo</span>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>
                        </div>
                    </StepWrap>
                )}
            </div>

            <div className="fixed bottom-0 inset-x-0 bg-bone/95 backdrop-blur-md border-t-2 border-ink">
                <div className="max-w-2xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
                    <button onClick={back} disabled={step === 0} className="btn-secondary text-sm disabled:opacity-30" data-testid="quiz-back">
                        <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
                    </button>
                    <button onClick={next} disabled={submitting} className="btn-saffron text-sm" data-testid="quiz-next">
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
                            : step === STEPS.length - 1 ? <>See my assistant <ArrowRight className="w-4 h-4" /></>
                                : <>Continue <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </div>
            </div>
        </div>
    );
}

function StepWrap({ title, sub, children }) {
    return (
        <div className="animate-fade-up">
            <h1 className="font-heading font-light text-3xl sm:text-4xl leading-tight tracking-tight">{title}</h1>
            {sub && <p className="mt-3 text-base text-muted2">{sub}</p>}
            <div className="mt-7">{children}</div>
        </div>
    );
}

function Choices({ value, onChange, options, testId }) {
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            {options.map(opt => (
                <button
                    key={opt}
                    type="button"
                    className={`choice-card ${value === opt ? "selected" : ""}`}
                    onClick={() => onChange(opt)}
                    data-testid={`choice-${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                    <span className="flex items-center justify-between">
                        <span>{opt}</span>
                        {value === opt && <Check className="w-4 h-4 text-ink" />}
                    </span>
                </button>
            ))}
        </div>
    );
}

function MultiChoices({ values, onToggle, options, testId }) {
    return (
        <div className="grid sm:grid-cols-2 gap-3">
            {options.map(opt => {
                const selected = values.includes(opt);
                return (
                    <button
                        key={opt}
                        type="button"
                        className={`choice-card ${selected ? "selected" : ""}`}
                        onClick={() => onToggle(opt)}
                        data-testid={`choice-${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                        <span className="flex items-center justify-between">
                            <span>{opt}</span>
                            {selected && <Check className="w-4 h-4 text-ink" />}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
